<script>
import { ref } from 'vue';

import { LpBasicLink } from '@thehotelsnetwork/lampo';

// Components
import BmChartView from './ChartView.vue';

import useSectionState from '@/state/useSectionState';
import BmControlsHeader from '@/components/ControlsHeader/ControlsHeader.vue';
import StatusBox from '@/components/StatusBox/StatusBox.vue';

import bdLogger from '@/utils/bdLogger';
import { techTrackingCatch } from '@/modules/techTracking';

export default {
  components: {
    'bm-controls-header': BmControlsHeader,
    'bm-chart-view': BmChartView,
    'bm-status-box': StatusBox,
    LpBasicLink,
  },
  props: {
    chart: {
      type: Object,
      required: true,
    },
  },
  emits: ['changeInterval', 'changeCompareFirstCompset', 'changeCompareSecondCompset', 'changeSort', 'handleShowAll'],
  setup(props, { emit }) {
    const {
      compsetNames,
      intervals,
    } = useSectionState();

    const controlsUpdatedKey = ref(0);

    const onChartUpdated = () => {
      controlsUpdatedKey.value += 1;
    };

    // Chart actions
    const changeInterval = (interval) => {
      emit('changeInterval', intervals.filter((m) => m.name === interval)[0].name);
    };

    const changeCompareFirstCompset = (compset) => {
      onChartUpdated();
      emit('changeCompareFirstCompset', compset);
    };

    const changeCompareSecondCompset = (compset) => {
      onChartUpdated();
      emit('changeCompareSecondCompset', compset);
    };

    const changeDefaultSort = () => {
      emit('changeSort', !props.chart.defaultSort);
    };

    const handleShowAll = () => {
      emit('handleShowAll');
    };

    const onChartError = (err) => {
      techTrackingCatch({ err });
      // log spec for easy debugging
      bdLogger.warn(
        `
* Chart ID: ${props.chart.id}
* ${props.chart.err}
* Spec:
${JSON.stringify(props.chart.spec)}
`,
      );
    };

    return {
      // controls
      intervals,
      changeInterval,
      compsetNames,
      changeCompareSecondCompset,
      changeCompareFirstCompset,
      changeDefaultSort,
      handleShowAll,
      // Events
      onChartError,
      onChartUpdated,
      controlsUpdatedKey,
    };
  },
};
</script>
<template>
  <LpCard class="bm-card wrapper-highlight flex flex-col gap-9 py-10 px-12">
    <!-- Chart controls -->
    <bm-controls-header
      :id="chart.id"
      :key="controlsUpdatedKey"
      :type="'chart'"
      :title="chart.title"
      :tp-config="chart.tooltipConfig"
      :loading="chart.loading"
      :intervals="intervals"
      :selected-interval="chart.selectedInterval"

      :change-interval="changeInterval"
      :compset-names="compsetNames"
      :compare-first-compset="chart.compareFirstCompset"
      :compare-second-compset="chart.compareSecondCompset"
      :change-compare-first-compset="changeCompareFirstCompset"

      :change-compare-second-compset="changeCompareSecondCompset"
      :default-sort="chart.defaultSort"

      :change-default-sort="changeDefaultSort"
    />
    <!-- Chart view -->
    <template v-if="chart.ready">
      <bm-chart-view
        :id="chart.id"
        :spec="chart.spec"
        :currency="chart.currency"

        @error="onChartError"
        @update="onChartUpdated"
      />

      <div
        v-if="chart.showAll !== undefined"
        class="flex items-center justify-center text-sm font-medium"
      >
        <LpBasicLink
          @click="handleShowAll"
        >
          {{ $t(chart.showAll ? 'Show less' : 'Show all') }}
        </LpBasicLink>
      </div>
    </template>

    <bm-status-box
      v-else
      :loading="chart.loading"
      :error="chart.error"
    />
  </LpCard>
</template>
